import React from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { Question, Serializer, ElementFactory } from "survey-core";
import { ReactQuestionFactory , SurveyQuestionElementBase } from "survey-react-ui";
import { PropertyGridTriggerValueInLogicEditor } from "survey-creator-core";

import { APPOINTMENT_SETTING_ICON } from "./icon";

export const APPOINTMENT_SETTING_TYPE = "appointmentsetting";
export const APPOINTMENT_SETTING_NAME = "calendarId";
export const APPOINTMENT_SETTING_TITLE = "Appointment Setting";

function visitSurveyElements(elements, visitor) {
  if (!Array.isArray(elements)) return;
  for (const el of elements) {
    if (el.type === "panel" && Array.isArray(el.elements)) {
      visitSurveyElements(el.elements, visitor);
      continue;
    }
    visitor(el);
  }
}

export function applyAppointmentSettingConstraints(question) {
  if (!question || question.getType?.() !== APPOINTMENT_SETTING_TYPE) return;
  question.name = APPOINTMENT_SETTING_NAME;
  question.title = APPOINTMENT_SETTING_TITLE;
  question.showTitle = false;
}

export function normalizeAppointmentSettingInSurvey(survey) {
  survey
    ?.getAllQuestions?.()
    .filter((q) => q.getType?.() === APPOINTMENT_SETTING_TYPE)
    .forEach(applyAppointmentSettingConstraints);
}

export function normalizeAppointmentSettingInJson(surveyJson, liveSurvey = null) {
  if (!surveyJson || typeof surveyJson !== "object") return surveyJson;

  const liveQuestion =
    liveSurvey?.getQuestionByName?.(APPOINTMENT_SETTING_NAME) ||
    liveSurvey
      ?.getAllQuestions?.()
      .find((q) => q.getType?.() === APPOINTMENT_SETTING_TYPE);

  if (Array.isArray(surveyJson.pages)) {
    for (const page of surveyJson.pages) {
      visitSurveyElements(page.elements, (el) => {
        if (el.type !== APPOINTMENT_SETTING_TYPE) return;
        el.name = APPOINTMENT_SETTING_NAME;
        el.title = APPOINTMENT_SETTING_TITLE;
        el.showTitle = false;
        if (liveQuestion?.value != null && liveQuestion.value !== "") {
          el.defaultValue = String(liveQuestion.value);
        }
      });
    }
  }

  return surveyJson;
}

// ============================================================
// Module-level choices store
// ============================================================
let _appointmentChoices = [];

export function setAppointmentChoices(choices) {
  _appointmentChoices = Array.isArray(choices) ? choices : [];
}

export function getAppointmentChoices() {
  return _appointmentChoices;
}

// ============================================================
// Question Model
// ============================================================
class QuestionAppointmentSettingModel extends Question {
  getType() {
    return APPOINTMENT_SETTING_TYPE;
  }

  get allowMultiple() {
    return this.getPropertyValue("allowMultiple") || false;
  }
  set allowMultiple(val) {
    this.setPropertyValue("allowMultiple", val);
  }

  get choices() {
    return getAppointmentChoices();
  }
}

// ============================================================
// React Component - MUI Select Dropdown
// ============================================================
class SurveyQuestionAppointmentSetting extends SurveyQuestionElementBase {
  constructor(props) {
    super(props);
    this.state = {};
  }

  get question() { return this.questionBase; }
  get value() { return this.question.value; }
  get choices() { return this.question.choices; }
  get isMultiple() { return this.question.allowMultiple; }
  get isDisabled() { return this.question.getPropertyValue("readOnly") === true; }

  isSelected = (val) => this.isMultiple
    ? Array.isArray(this.value) && this.value.includes(val)
    : this.value === val;

  getDisplayText = () => {
    const choices = this.choices;
    if (this.isMultiple) {
      return Array.isArray(this.value) && this.value.length > 0
        ? this.value.map(v => choices.find(c => c.value === v)?.text).filter(Boolean).join(", ")
        : "Select options...";
    }
    return choices.find(c => c.value === this.value)?.text || "Select an option...";
  };

  handleMuiChange = (e) => {
    if (this.isDisabled) return;
    this.question.value = e.target.value;
    this.setState({});
  };

  renderElement() {
    const disabled = this.isDisabled;
    const value = this.isMultiple ? (this.value || []) : (this.value || "");

    return (
      <FormControl fullWidth variant="outlined" size="small" className="appointment-setting-wrapper">
        <Select
          value={value}
          multiple={this.isMultiple}
          disabled={disabled}
          onChange={this.handleMuiChange}
          displayEmpty
          renderValue={this.getDisplayText}
          input={<OutlinedInput notched={false} />}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                borderRadius: 6
              }
            },
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" }
          }}
        >
          {this.choices.map(choice => (
            <MenuItem key={choice.value} value={choice.value}>
              {this.isMultiple && (
                <Checkbox checked={this.isSelected(choice.value)} size="small" />
              )}
              <ListItemText primary={choice.text} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
}

// ============================================================
// Registration Function (called by registry)
// ============================================================
export function registerAppointmentSetting(apiChoices = []) {
  const TYPE = APPOINTMENT_SETTING_TYPE;

  // Skip if already registered
  if (ElementFactory.Instance.getAllTypes().includes(TYPE)) {
    return { alreadyRegistered: true };
  }

  setAppointmentChoices(apiChoices);

  // ── Patch Logic Editor value renderer ─────────────────────────────────────
  // PropertyGridTriggerValueInLogicEditor.getJSON copies the target question's
  // type verbatim (e.g. "appointmentsetting"). SurveyJS has no built-in
  // knowledge of this type so it renders a plain text input, and the selected
  // text gets serialised as the trigger value instead of the choice's value.
  // We patch getJSON to return a proper "dropdown" JSON with our calendar
  // choices whenever the target question is of type appointmentsetting.
  const _origGetJSON = PropertyGridTriggerValueInLogicEditor.prototype.getJSON;
  PropertyGridTriggerValueInLogicEditor.prototype.getJSON = function(obj, prop, options) {
    const json = _origGetJSON.call(this, obj, prop, options);
    if (json && json.type === TYPE) {
      return {
        type: "dropdown",
        choices: getAppointmentChoices().map(c => ({ value: c.value, text: c.text }))
      };
    }
    return json;
  };
  // ──────────────────────────────────────────────────────────────────────────

  Serializer.addClass(
    TYPE,
    [{ name: "allowMultiple:boolean", default: false, category: "general", displayName: "Allow Multiple Selection" }],
    () => new QuestionAppointmentSettingModel(""),
    "question"
  );

  ElementFactory.Instance.registerElement(TYPE, (name) => new QuestionAppointmentSettingModel(name));
  ReactQuestionFactory.Instance.registerQuestion(TYPE, (props) => React.createElement(SurveyQuestionAppointmentSetting, props));

  console.log(`[SurveyJS] ${TYPE} registered`);
  return { alreadyRegistered: false };
}

// ============================================================
// Toolbox Config (used by registry)
// ============================================================
export const appointmentSettingConfig = {
  id: APPOINTMENT_SETTING_TYPE,
  name: APPOINTMENT_SETTING_TYPE,
  title: "Appointment Setting",
  iconSvg: APPOINTMENT_SETTING_ICON,
  defaultJson: {
    type: APPOINTMENT_SETTING_TYPE,
    name: APPOINTMENT_SETTING_NAME,
    title: APPOINTMENT_SETTING_TITLE,
    showTitle: true,
    allowMultiple: false,
    visible: false,
  }
};
